import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormLayout,
  FormMessage,
  FormSection,
  Input,
  SegmentedControl,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Textarea,
  toast,
} from '@avila-roffe/components-web';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useApplications } from '../../../hooks/use-applications';
import { useTenants } from '../../../hooks/use-tenants';
import type { CatalogItem } from '../../agents/AgentRefsBindingPanel';
import { ToolSkillPickerDialog } from '../../agents/components/ToolSkillPickerDialog';
import { useTools } from '../../tools/hooks/use-tools';
import { useUpdateSkillMetadata } from '../hooks/use-skills';
import type { Skill } from '../types';

// ─── Options ──────────────────────────────────────────────────────────────────

const SCOPE_OPTIONS = [
  { value: 'PUBLIC', label: 'Public' },
  { value: 'APPLICATION', label: 'Application' },
] as const;

// ─── Schema (no status — managed through releases) ────────────────────────────

const schema = z.object({
  displayName: z.string().min(1, 'Display name is required'),
  shortDescription: z.string().optional(),
  scope: z.enum(['PUBLIC', 'APPLICATION', 'TENANT']),
  appSlug: z.string().optional(),
  tenantSlug: z.string().optional(),
  tags: z.array(z.string()),
  promptSuggestions: z.array(z.string()),
  toolRefs: z.array(z.string()),
});

type FormValues = z.infer<typeof schema>;

function buildDefaults(skill: Skill): FormValues {
  return {
    displayName: skill.interface.displayName || '',
    shortDescription: skill.interface.shortDescription || '',
    scope: skill.scope,
    appSlug: skill.appSlug,
    tenantSlug: skill.tenantSlug ?? '',
    tags: [...skill.tags],
    promptSuggestions: [...(skill.interface.promptSuggestions ?? [])],
    toolRefs: [...(skill.toolRefs ?? [])],
  };
}

// ─── Tag chip input ───────────────────────────────────────────────────────────

function TagChipInput({
  value,
  onChange,
  placeholder,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  function addTag(raw: string) {
    const tag = raw.trim().replace(/,+$/, '');
    if (tag && !value.includes(tag)) onChange([...value, tag]);
  }

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground"
            >
              {tag}
              <button
                type="button"
                aria-label={`Remove ${tag}`}
                className="rounded-full hover:bg-muted-foreground/20"
                onClick={() => onChange(value.filter((t) => t !== tag))}
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <Input
        placeholder={placeholder ?? 'Type and press Enter…'}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ',') && e.currentTarget.value.trim()) {
            e.preventDefault();
            addTag(e.currentTarget.value);
            e.currentTarget.value = '';
          }
        }}
        onBlur={(e) => {
          if (e.currentTarget.value.trim()) {
            addTag(e.currentTarget.value);
            e.currentTarget.value = '';
          }
        }}
      />
    </div>
  );
}

// ─── SkillOverviewTab ─────────────────────────────────────────────────────────

export function SkillOverviewTab({
  skill,
  isSuperAdmin,
  onDelete,
  isDeleting,
}: {
  skill: Skill;
  isSuperAdmin: boolean;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const updateMutation = useUpdateSkillMetadata(skill.appSlug, skill.name);
  const { data: applications = [] } = useApplications();
  const { data: tenantsData } = useTenants({ limit: 500 });
  const tenants = tenantsData?.items ?? [];
  const { data: allTools = [] } = useTools();
  const [toolPickerOpen, setToolPickerOpen] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: buildDefaults(skill),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when skill id changes
  useEffect(() => {
    form.reset(buildDefaults(skill));
  }, [skill.id]);

  const isDirty = form.formState.isDirty;
  const isSaving = updateMutation.isPending;
  const watchedScope = form.watch('scope');

  // Sync appSlug when scope changes to PUBLIC
  useEffect(() => {
    if (watchedScope === 'PUBLIC') form.setValue('appSlug', undefined);
  }, [watchedScope, form]);

  async function onSubmit(data: FormValues) {
    try {
      await updateMutation.mutateAsync({
        scope: data.scope,
        appSlug: data.scope !== 'PUBLIC' ? data.appSlug : undefined,
        tenantSlug: data.scope === 'TENANT' ? data.tenantSlug || null : undefined,
        tags: data.tags,
        compatibleTools: [],
        toolRefs: data.toolRefs,
        interface: {
          displayName: data.displayName,
          shortDescription: data.shortDescription ?? '',
          promptSuggestions: data.promptSuggestions,
        },
      });
      form.reset(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save skill');
    }
  }

  return (
    <Form {...form}>
      <FormLayout
        className="sm:pt-4"
        actionsPosition="sticky"
        isDirty={isDirty}
        cancelAction={{
          label: 'Discard',
          type: 'button',
          onClick: () => form.reset(buildDefaults(skill)),
        }}
        submitAction={{
          label: isSaving ? 'Saving…' : 'Save changes',
          type: 'button',
          disabled: isSaving,
          onClick: () => void form.handleSubmit(onSubmit)(),
        }}
      >
        {/* ── Configuration ─────────────────────────────────────────────── */}
        <FormSection title="Configuration" asCard>
          <div className="grid grid-cols-2 gap-4 text-sm mb-2">
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Slug</p>
              <p className="font-mono font-medium">{skill.name}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Cannot be changed</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Version</p>
              <p className="font-mono">v{skill.version}</p>
            </div>
          </div>

          <FormField
            control={form.control}
            name="displayName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Display Name</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="My Skill" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="shortDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Short Description</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    rows={2}
                    className="resize-none"
                    placeholder="What this skill does in one sentence"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="scope"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Scope</FormLabel>
                <FormControl>
                  <SegmentedControl
                    options={SCOPE_OPTIONS}
                    value={field.value === 'TENANT' ? 'APPLICATION' : field.value}
                    onValueChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {watchedScope !== 'PUBLIC' && (
            <FormField
              control={form.control}
              name="appSlug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Application</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value ?? ''}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select application" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {applications.map((app) => (
                        <SelectItem key={app.slug} value={app.slug}>
                          {app.name || app.slug}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {watchedScope === 'TENANT' && (
            <FormField
              control={form.control}
              name="tenantSlug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tenant</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value ?? ''}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select tenant" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {tenants.map((t) => (
                        <SelectItem key={t.slug} value={t.slug}>
                          {t.name || t.slug}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="tags"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tags</FormLabel>
                <FormControl>
                  <TagChipInput
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Add tag and press Enter…"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="promptSuggestions"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Prompt Suggestions</FormLabel>
                <FormControl>
                  <TagChipInput
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Add suggestion and press Enter…"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {(skill.gitPath || skill.gitSha) && (
            <div className="grid grid-cols-1 gap-2 text-sm">
              {skill.gitPath && (
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Git Path</p>
                  <p className="font-mono text-xs">{skill.gitPath}</p>
                </div>
              )}
              {skill.gitSha && (
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Git SHA</p>
                  <p className="font-mono text-xs">{skill.gitSha}</p>
                </div>
              )}
            </div>
          )}
        </FormSection>

        {/* ── Tools ────────────────────────────────────────────────────────
            toolRefs = tools this skill bundles and injects into the agent
            context at runtime. Loading a skill grants its tools automatically. */}
        <FormSection title="Tools" asCard>
          <p className="text-xs text-muted-foreground -mt-1">
            Tools bound here are automatically available when this skill is loaded in a session.
          </p>
          {form.watch('toolRefs').length > 0 ? (
            <div className="space-y-1">
              {form.watch('toolRefs').map((ref) => {
                const tool = allTools.find((t) => t.name === ref);
                return (
                  <div
                    key={ref}
                    className="flex items-center justify-between rounded-lg border bg-card px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{tool?.name ?? ref}</p>
                      {tool?.description && (
                        <p className="text-xs text-muted-foreground truncate">{tool.description}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      aria-label={`Remove ${ref}`}
                      className="ml-2 shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                      onClick={() =>
                        form.setValue(
                          'toolRefs',
                          form.getValues('toolRefs').filter((r) => r !== ref),
                          { shouldDirty: true },
                        )
                      }
                    >
                      <X className="size-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No tools bound. Add tools to expose them automatically.
            </p>
          )}
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => setToolPickerOpen(true)}
          >
            Add tools
          </Button>
        </FormSection>

        <ToolSkillPickerDialog
          title="tools"
          open={toolPickerOpen}
          onOpenChange={setToolPickerOpen}
          catalog={allTools.map<CatalogItem>((t) => ({
            value: t.name,
            label: t.name,
            description: t.description,
            meta: t.appSlug,
            scope: t.scope,
            status: t.status,
          }))}
          selectedRefs={form.watch('toolRefs')}
          isSaving={false}
          onConfirm={async (newRefs) => {
            form.setValue('toolRefs', newRefs, { shouldDirty: true });
            setToolPickerOpen(false);
          }}
        />

        {/* ── Danger zone ───────────────────────────────────────────────── */}
        <FormSection title="Danger Zone" asCard>
          <p className="text-sm text-muted-foreground">
            Deleting a skill is permanent and cannot be undone.
          </p>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={onDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Spinner className="mr-1.5 size-3.5" />
            ) : (
              <Trash2 className="mr-1.5 size-3.5" />
            )}
            Delete skill
          </Button>
        </FormSection>
      </FormLayout>
    </Form>
  );
}
